import { View, Text, Pressable, Linking } from 'react-native'
import React, { useEffect, useState } from 'react'
import Toast from 'react-native-toast-message'
import { StyleSheet } from 'react-native-unistyles'
import { Table } from '@/components/General/Table'
import Avatar from '@/components/People/Avatar'
import { DateTime } from 'luxon'
import { Query } from '@/utils/react-query/Queries'
import usePlatform from '@/utils/hooks/usePlatform'
import { useNavigation, useRouter } from 'expo-router'
import { useFilter } from '@/data/store/useFilterStore'
import PeopleAdvancedFilter from '@/components/Filters/PeopleAdvancedFilter'
import { exportMissedReport } from '@/data/reports'
import Unicon from '@/components/General/Unicon'
import ExportFieldsModal from '@/components/General/ExportFieldsModal'
import { openURL } from '@/utils/linking'

export default function index() {
    const [searchText, setSearchText] = React.useState("")
    const [exportModalOpen, setExportModalOpen] = useState(false)
    const [filters, setFilters] = React.useState([
        {
            label: 'Days',
            inputType: 'dropdown',
            options: [
                { label: '30 Days', value: 30 },
                { label: '60 Days', value: 60 },
                { label: '90 Days', value: 90 },
                { label: '180 Days', value: 180 },
                { label: '365 Days', value: 365 },
            ],
            value: 30,
            defaultValue: 30
        },
        {
            label: 'Event',
            inputType: 'sheetselector',
            sheetType: 'events',
            value: null,
            defaultValue: null
        }
    ])

    const numberOfDays = filters.find(f => f.label === 'Days')?.value || 30
    const eventID = filters.find(f => f.label === 'Event')?.value?.id || null

    const {isLandscape} = usePlatform()
    const router = useRouter()
    const filterState = useFilter('missed-report');
    const navigation = useNavigation();

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => (
                <View style={{flexDirection:"row", gap:10, alignItems:"center"}}>
                    <PeopleAdvancedFilter filterId="missed-report" />
                    <Pressable onPress={() => setExportModalOpen(true)}>
                        <Unicon name="cloud-download-outline" size={22} colorVariant="text" />
                    </Pressable>
                </View>
            ),
        });
    }, [navigation])

    const handleExport = (selectedFields) => {
        exportMissedReport({
            days: numberOfDays,
            eventID,
            filters: {values: filterState.peopleFilters, logic: filterState.filterLogic},
            searchText,
            selectedFields
        })
    }

    const { data, isLoading } = Query.reports.missed({days: numberOfDays, eventID, filters: {values:filterState.peopleFilters, logic: filterState.filterLogic}, searchText})

    const columns = [
        {value: 'Name', width:isLandscape ? undefined : 250, renderer: (row) => {
            const item = row.stats;
            
            return <View style={styles.personHeadingContainer}>
                <Avatar size={30} profile={item} disableBorder/>
                <View style={{paddingLeft:10}}>
                    <Pressable onPress={() => router.push(`/people/${item.id}`)}><Text style={styles.personHeading}>{item.firstName} {item.lastName}</Text></Pressable>
                    <Pressable onPress={() => Linking.openURL(`mailto:${item.email}`).catch(() => Toast.show({ type: 'error', text2: 'No email app found.' }))}><Text style={styles.personSubHeading}>{item.email}</Text></Pressable>
                </View>
            </View>
        }},
        {value: 'Phone', renderer: (row) => {
            const item = row.stats;
            return (
                <View style={{gap:5}}>
                    {(item.Phones || []).map((p, i) => <Pressable key={p.phoneNumber + i} onPress={() => openURL(`tel:${p.phoneNumber.replace(/ /g, '')}`)}><Text style={styles.personSubHeading}>{p.phoneNumber}</Text></Pressable>)}
                </View>
            );
        }},
        {value: 'Last Attended', renderer: (row) => {
            const item = row.stats;
            return <Text style={styles.colText()}>{DateTime.fromISO(item.lastVisit).toLocaleString(DateTime.DATE_MED)}</Text>
        }},
        {value: 'Days Since Last Visit', renderer: (row) => {
            const item = row.stats;
            const days = Math.abs(Math.floor(DateTime.fromISO(item.lastVisit).diffNow('days').days))
            const weeks = Math.floor(days / 7)
            const remainingDays = days % 7

            const conditionalColor = days < 14 
                ? 'textSecondary'
                : days < 21
                    ? 'warning'
                    : 'error'
            return <Text style={styles.colText(conditionalColor)}>{weeks} weeks {remainingDays} days</Text>
        }}
    ]

    return (
        <View style={{flex:1, paddingTop:10}}>
            <ExportFieldsModal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onExport={handleExport}
                title="Export Missed Report"
            />
            <Table 
                title="Missed at least 7 days"
                description={`People who have not attended ${eventID ? filters.find(f => f.label === 'Event')?.value?.name : "any event"} in 7 days, but have attended it at least once in the last ${numberOfDays} days`}
                columns={columns} 
                data={data?.filter(item => item.stats.firstName.toLowerCase().includes(searchText.toLowerCase()) || item.stats.lastName.toLowerCase().includes(searchText.toLowerCase()))} 
                isLoading={isLoading} 
                keyExtractor={(item) => item.stats.id}
                filters={filters}
                onFiltersChange={setFilters}
                onSearchChange={setSearchText}
                removeClippedSubviews
            />
        </View>
    )
}

const styles = StyleSheet.create(({colors, fonts}) => ({
    personHeadingContainer: {
        flexDirection:"row", 
        alignItems:"center", 
        gap:5
    },
    personHeading: {
        fontSize:14, 
        fontFamily:fonts.heading, 
        color:colors.text,
        _web:{
            cursor:"pointer",
            _hover:{
                color:colors.secondary,
            }
        }
    },
    personSubHeading: {
        color:colors.primary,
        fontFamily:fonts.body,
        fontSize:12,
        fontStyle:"italic",
        _web:{
            cursor:"pointer",
            _hover:{
                color:colors.secondary,
            }
        }
    },
    colText: (color) => ({
        fontFamily:fonts.body,
        fontSize:14,
        color:colors[color] || colors.textSecondary
    })
}))
